import {
  cloneElement,
  isValidElement,
  type ReactElement,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  useMemo,
} from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import type { OverlayTriggerType } from 'react-bootstrap/esm/OverlayTrigger';

type TriggerButtonProps = {
  onClick?: (event: ReactMouseEvent<HTMLElement>) => void;
};

type BaseTooltipProps = {
  id?: string;
  text: ReactNode;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'auto';
  className?: string;
  trigger?: OverlayTriggerType | OverlayTriggerType[];
  blurOnClick?: boolean;
  children: ReactElement;
} & {};

const BaseTooltip = ({
  id,
  text,
  placement = 'top',
  className = 'responder-tooltip',
  trigger = ['hover', 'focus'],
  blurOnClick = false,
  children,
}: BaseTooltipProps) => {
  const normalizedTrigger = useMemo<OverlayTriggerType[]>(() => {
    const triggerList = Array.isArray(trigger) ? trigger : [trigger];
    const deduped = Array.from(new Set(triggerList.filter(Boolean)));
    if (deduped.includes('hover') && !deduped.includes('focus')) {
      deduped.push('focus');
    }
    return deduped;
  }, [trigger]);

  const childNode: ReactElement =
    blurOnClick && isValidElement<TriggerButtonProps>(children)
      ? cloneElement(children, {
          onClick: (event: ReactMouseEvent<HTMLElement>) => {
            children.props?.onClick?.(event);
            event?.currentTarget?.blur?.();
          },
        })
      : children;

  return (
    <OverlayTrigger
      trigger={normalizedTrigger}
      placement={placement}
      overlay={
        <Tooltip id={id} className={className}>
          {text}
        </Tooltip>
      }
    >
      {childNode}
    </OverlayTrigger>
  );
};

export default BaseTooltip;
